import axios, { AxiosResponse } from 'axios'
import { trackBookingCreated } from 'helpers/analytics'
import { AmendBooking } from './models/BookingDetails.type'
import {
  BookingPrecheckRequestData,
  BookingPrecheckResponse,
} from './models/BookingPrecheck.type'
import {
  BookingSearchQueryParams,
  BookingSearchRequestBody,
} from './models/BookingSearch'
import { clean } from './utility/jsonFunctions'
import { getUserToken } from './utility/localStorageFunctions'

const BASE_URL = `${process.env.REACT_APP_BASE_API_URL}/bookings-api/bookings`

async function getPolicyStatus(
  bookingReference: string,
  journeyReference: string,
) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'GET',
    url: BASE_URL.concat(
      `/${bookingReference}/journeys/${journeyReference}/retrievePolicyStatus`,
    ),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function bookingsSearch(
  data: BookingSearchRequestBody,
  pagination: BookingSearchQueryParams,
) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'post',
    url: BASE_URL.concat(
      `/search?limit=${pagination.pageSize}${
        pagination.pageIndex >= 1
          ? `&offset=${pagination.pageIndex * pagination.pageSize}`
          : ``
      }`,
    ),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  return response.data
}

async function getBookingJobs(bookingId: number) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${bookingId}/jobs`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

async function getBooking(bookingId: number) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${bookingId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

async function getBookingWithPolicy(bookingId: number) {
  const token = await getUserToken('bookings')

  const bookingResponse = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${bookingId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const bookingData = bookingResponse.data

  const policyResponse = await axios({
    method: 'GET',
    url: BASE_URL.concat(
      `/${bookingData.bookingReference}/journeys/${bookingData.journeyReference}/retrievePolicyStatus`,
    ),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const policyData = policyResponse.data

  bookingData.policy = {
    cancellation: {
      fee: policyData.cancellationCharge,
      possible: policyData.isCancellable,
    },
  }

  console.log('<><>', bookingData)
  return bookingData
}

async function createBooking(quotedBooking: any) {
  clean(quotedBooking)
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'post',
    url: BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...quotedBooking,
      },
    },
  }).catch((err) => {
    const message =
      err?.response?.data?.error?.message || 'Unknown error occurred'
    throw new Error(message)
  })
  trackBookingCreated(quotedBooking, response.data)
  return response
}

async function precheckBooking(
  precheckData: BookingPrecheckRequestData,
): Promise<AxiosResponse<BookingPrecheckResponse>> {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'post',
    url: BASE_URL.concat(`/precheck`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...precheckData,
      },
    },
  })

  return response
}

async function cancelBooking(bookingId: any) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'delete',
    url: BASE_URL.concat(`/${bookingId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function cancelBookingReference(bookingReference: any) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'delete',
    url: BASE_URL.concat(`/cancelBooking/${bookingReference}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function cancelVehicle(jobReference: string) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'delete',
    url: BASE_URL.concat(`/cancelVehicle/${jobReference}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function vehicleAudit(bookingId: any, vehicleId: any) {
  const token = await getUserToken('bookings')

  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${bookingId}/vehicles/${vehicleId}/history`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  console.log('API: getting vehicle history', response)
  return response
}

async function resendCommunication(
  bookingId: number,
  communicationType: string,
) {
  const token = await getUserToken('bookings')
  console.log('resendCommunication', bookingId, communicationType)
  const response = await axios({
    method: 'post',
    url: BASE_URL.concat(`/${bookingId}/resendCommunication`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: { resendCommunicationTypeName: communicationType },
    },
  })
  return response
}

async function updateBooking(bookingId: number, updateData: AmendBooking) {
  clean(updateData, ['specialInstructions', 'flightDetails'])
  try {
    const token = await getUserToken('bookings')
    const response = await axios({
      method: 'patch',
      url: BASE_URL.concat(`/${bookingId}/`),
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        data: {
          ...updateData,
        },
      },
    })
    return response
  } catch (error: any) {
    if (
      error.response.status === 400 &&
      error.response.data.error.validationErrors
    ) {
      return Promise.reject({
        message:
          'Contact Information provided is invalid, please check and try again.',
      })
    } else {
      return Promise.reject({
        message:
          'There was an error updating, please try again. If this error persists, please contact your system administrator.',
      })
    }
  }
}

export const bookingsAPI = {
  createBooking,
  precheckBooking,
  cancelBooking,
  cancelBookingReference,
  cancelVehicle,
  vehicleAudit,
  bookingsSearch,
  getBooking,
  getBookingWithPolicy,
  getBookingJobs,
  getUserToken,
  resendCommunication,
  getPolicyStatus,
  updateBooking,
}
